/**
 * Value formatters shared by every T&C template surface.
 *
 * Template values are strings by the time the grammar sees them, so this is
 * where a number becomes "$1,234.50" and an hour becomes "9:00 AM".
 *
 * ### Deterministic on purpose — no `Intl`
 *
 * `toLocaleString` output varies with the runtime's ICU build and the viewer's
 * locale, and these strings are not display-only: the check-in ones are frozen
 * into a legal acceptance record, and Python has to produce them byte-for-byte
 * (`sm4rt-public-api/core/services/template_variables.py`). A month name that
 * depends on which machine rendered it is not something to freeze. So the
 * month names and the currency grouping are written out here, and the parity
 * fixture pins them.
 */

import type { BusinessHoursConfig } from '../contracts/timeWindow';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LONG = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** `0` and `24` are both midnight; whole hours only, matching the schema. */
export function formatHour(hour: number): string {
  if (hour === 0 || hour === 24) return '12:00 AM';
  if (hour === 12) return '12:00 PM';
  if (hour < 12) return `${hour}:00 AM`;
  return `${hour - 12}:00 PM`;
}

/** `[1,2,3,4,5]` → "Mon–Fri"; `[1,3,5]` → "Mon, Wed, Fri". */
export function formatWorkDaysCompact(days: readonly number[]): string {
  if (!days.length) return '';
  const sorted = [...days].sort((a, b) => a - b);
  if (isContiguous(sorted)) {
    return `${DAY_SHORT[sorted[0]]}–${DAY_SHORT[sorted[sorted.length - 1]]}`;
  }
  return sorted.map((d) => DAY_SHORT[d]).join(', ');
}

/** `[1,2,3,4,5]` → "Monday to Friday". */
export function formatWorkDaysLong(days: readonly number[]): string {
  if (!days.length) return '';
  const sorted = [...days].sort((a, b) => a - b);
  if (isContiguous(sorted)) {
    return `${DAY_LONG[sorted[0]]} to ${DAY_LONG[sorted[sorted.length - 1]]}`;
  }
  return sorted.map((d) => DAY_LONG[d]).join(', ');
}

function isContiguous(sorted: readonly number[]): boolean {
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i] !== sorted[i - 1] + 1) return false;
  }
  return sorted.length >= 2;
}

/**
 * `"2026-05-19"` (or a full ISO timestamp) → `"19 May 2026"`.
 *
 * Reads the date parts out of the string rather than constructing a `Date`:
 * a timestamp parsed as UTC and rendered in local time lands on the previous
 * day for anyone east of Greenwich, which is everyone here. Callers convert to
 * the Australia/Sydney calendar date before this sees it.
 */
export function formatLongDate(value: string | null | undefined): string {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const [, year, month, day] = match;
  const monthName = MONTHS[Number(month) - 1];
  if (!monthName) return value;
  return `${Number(day)} ${monthName} ${Number(year)}`;
}

/** `1234.5` → `"$1,234.50"`; `-5` → `"-$5.00"`. */
export function formatCurrencyAud(amount: number | null | undefined): string {
  const value = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
  const negative = value < 0;
  const fixed = Math.abs(value).toFixed(2);
  const [whole, cents] = fixed.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}$${grouped}.${cents}`;
}

/** Every business-hours value, keyed by the path templates reference. */
export function businessHoursValues(
  hours: BusinessHoursConfig,
): Record<string, string> {
  return {
    businessHoursStart: formatHour(hours.businessStartHour),
    businessHoursEnd: formatHour(hours.businessEndHour),
    shoulderHoursStart: formatHour(hours.shoulderStartHour),
    shoulderHoursEnd: formatHour(hours.shoulderEndHour),
    workDays: formatWorkDaysCompact(hours.workDays),
    workDaysLong: formatWorkDaysLong(hours.workDays),
    timezone: hours.timezone,
  };
}
